import { useQuery } from '@tanstack/react-query';
import {
  CalendarOffIcon,
  CheckCircle2Icon,
  ChevronRightIcon,
  ExternalLinkIcon,
  FilePlusIcon,
  MegaphoneOffIcon,
  UserPlusIcon,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { useSnapshot } from 'valtio';

import { msCatalystApi } from '../../../../services/api/ms-catalyst-api.js';
import { authState } from '../../../../services/state/auth-state.jsx';
import { useQueryActiveCampaign } from '../../../../hooks/queries/use-active-campaign.jsx';
import {
  CAMPAIGN_COLORS,
  CAMPAIGN_STATES,
} from '../../../../utils/constants.js';
import { timeAgo } from '../../../../utils/dates.js';
import { greeting } from '../../../../utils/misc.js';

import Error from '../../../feedback/error.jsx';
import Loading from '../../../feedback/loading.jsx';
import { Badge } from '../../../ui/badge.jsx';
import { Button } from '../../../ui/button.jsx';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../../../ui/card.jsx';

const PENDING_INVOICES_PAGE_SIZE = 20;

const CAMPAIGN_CLOSED_MESSAGES = {
  [CAMPAIGN_STATES.YET_TO_START]: {
    title: 'A campanha ainda não começou',
    description:
      'Assim que a campanha arrancar, as faturas pendentes de aprovação aparecem aqui.',
  },
  [CAMPAIGN_STATES.READY_TO_DRAW]: {
    title: 'A campanha já fechou',
    description:
      'A campanha está a aguardar o sorteio, já não é possível inserir nem aprovar faturas.',
  },
  [CAMPAIGN_STATES.DRAW_CONCLUDED]: {
    title: 'A campanha já fechou',
    description:
      'O sorteio da campanha já foi realizado, já não é possível inserir nem aprovar faturas.',
  },
  [CAMPAIGN_STATES.CONCLUDED]: {
    title: 'A campanha já fechou',
    description:
      'Já não é possível inserir nem aprovar faturas nesta campanha.',
  },
};

/**
 * Renders a centered empty state with an icon, a title and an optional description.
 *
 * @param {Object} props
 * @param {import('lucide-react').LucideIcon} props.icon
 * @param {string} props.title
 * @param {string} [props.description]
 *
 * @return {JSX.Element}
 */
const EmptyState = ({ icon: Icon, title, description }) => {
  return (
    <div className="flex w-full flex-col items-center justify-center px-6 py-12 text-center">
      <Icon className="size-16 text-muted-foreground" />
      <h3 className="pt-6 text-xl font-medium">{title}</h3>
      {description ? (
        <p className="max-w-prose pt-2 text-sm text-muted-foreground">
          {description}
        </p>
      ) : null}
    </div>
  );
};

export default function DashboardBackofficeUser() {
  const { user } = useSnapshot(authState);

  const queryActiveCampaign = useQueryActiveCampaign();

  const campaign = queryActiveCampaign.data;

  const activeCampaignIsOpen =
    campaign?.state === CAMPAIGN_STATES.ONGOING ||
    campaign?.state === CAMPAIGN_STATES.POST_END_DATE;

  const queryPendingInvoices = useQuery({
    queryKey: ['dashboard_pending_invoices', campaign?.ROWID],
    queryFn: async () => {
      const { data: pendingInvoices } = await msCatalystApi.get(
        `invoices/paginate?status=Pendente&campaignId=${campaign?.ROWID}&order_by=CREATEDTIME,asc&limit=${PENDING_INVOICES_PAGE_SIZE}&offset=1`,
      );

      return pendingInvoices.data;
    },
    enabled: !!campaign?.ROWID && activeCampaignIsOpen,
  });

  const canOperate = !!campaign?.operatorPermissions && activeCampaignIsOpen;

  const pendingInvoices = queryPendingInvoices.data?.rows ?? [];
  const pendingInvoicesCount = queryPendingInvoices.data?.rowCount ?? 0;

  const campaignClosedMessage = campaign
    ? (CAMPAIGN_CLOSED_MESSAGES[campaign.state] ?? {
        title: 'A campanha não está aberta',
        description:
          'De momento não é possível inserir nem aprovar faturas nesta campanha.',
      })
    : null;

  return (
    <div className="flex flex-col">
      {/* Greetings Card */}
      <div className="flex flex-col items-start space-y-2 pb-6 md:flex-row md:items-center md:justify-between md:space-y-0">
        <div className="">
          <h2 className="text-3xl font-bold tracking-tight">
            {greeting()}, {user?.firstName} {user?.lastName} 👋
          </h2>
        </div>
        <div className="flex items-center space-x-2">
          {canOperate ? (
            <Link to="/backoffice/customers/create">
              <Button>
                <UserPlusIcon className="mr-2 size-4" />
                Criar Cliente
              </Button>
            </Link>
          ) : null}

          {canOperate ? (
            <Link to="/backoffice/invoices/create">
              <Button>
                <FilePlusIcon className="mr-2 size-4" />
                Inserir Fatura
              </Button>
            </Link>
          ) : null}
        </div>
      </div>

      {queryActiveCampaign.isLoading ? (
        <Loading />
      ) : queryActiveCampaign.isError ? (
        <Error />
      ) : !campaign ? (
        <Card>
          <EmptyState
            icon={MegaphoneOffIcon}
            title="Não existe nenhuma campanha ativa"
            description="Assim que uma campanha for ativada, as faturas pendentes de aprovação aparecem aqui."
          />
        </Card>
      ) : (
        <>
          <div className="flex flex-row items-center pb-4">
            <div
              className={`mr-2 size-2 rounded-full ${
                CAMPAIGN_COLORS[campaign.state] ?? 'bg-stone-600'
              }`}
            ></div>
            <h3 className="text-lg">{campaign.name}</h3>
          </div>

          {!activeCampaignIsOpen ? (
            <Card>
              <EmptyState
                icon={CalendarOffIcon}
                title={campaignClosedMessage.title}
                description={campaignClosedMessage.description}
              />
            </Card>
          ) : (
            <Card>
              <CardHeader>
                <CardTitle>Faturas Pendentes</CardTitle>
                <CardDescription>
                  Faturas desta campanha a aguardar aprovação.
                </CardDescription>
              </CardHeader>

              <CardContent>
                {queryPendingInvoices.isLoading ||
                queryPendingInvoices.isRefetching ? (
                  <Loading />
                ) : queryPendingInvoices.isError ||
                  queryPendingInvoices.isRefetchError ? (
                  <Error />
                ) : !pendingInvoices.length ? (
                  <EmptyState
                    icon={CheckCircle2Icon}
                    title="Não existem faturas pendentes"
                    description="Todas as faturas desta campanha já foram tratadas."
                  />
                ) : (
                  <>
                    <div className="space-y-0">
                      {pendingInvoices.map((item, index) => {
                        return (
                          <div
                            key={index}
                            className="flex items-center rounded-lg p-4 hover:bg-background"
                          >
                            <div className="space-y-1">
                              <p className="text mb-2 font-medium leading-none">
                                {item.customers.cc} - {item.customers.fullName}
                              </p>
                              <p className="text-sm leading-none">
                                {item.shops.name}
                              </p>

                              <div>
                                {item.invoices.proof === 'Receipt' ? (
                                  <Badge>Comprovativo</Badge>
                                ) : (
                                  <Badge variant="outline">
                                    Sem Comprovativo
                                  </Badge>
                                )}
                              </div>

                              <p className="text-sm text-muted-foreground">
                                {timeAgo(item.invoices.CREATEDTIME)}
                              </p>
                            </div>

                            <Button asChild variant="secondary" size="sm">
                              <Link
                                className="ml-auto"
                                to={`/backoffice/invoices/${item.invoices.ROWID}/edit`}
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                <ExternalLinkIcon className="size-3" />
                              </Link>
                            </Button>
                          </div>
                        );
                      })}
                    </div>
                    {pendingInvoicesCount > pendingInvoices.length && (
                      <div className="mt-4 flex w-full justify-center">
                        <Link
                          to={`/backoffice/invoices?campaignId=${campaign.ROWID}`}
                        >
                          <Button variant="ghost">
                            Ver todas as Faturas
                            <ChevronRightIcon className="ml-2 size-4" />
                          </Button>
                        </Link>
                      </div>
                    )}
                  </>
                )}
              </CardContent>
            </Card>
          )}
        </>
      )}
    </div>
  );
}
